import { useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';

import { getNavItems, NAV_ZONES } from './navigation.config';
import { useNavigation } from '../../hooks/useNavigation';
import { MOBILE_OFFCANVAS_ID } from './MobileNavbar';
import ThemeToggle from './ThemeToggle';
import {
  normalizeUnreadCount,
  resolveNotificationNavPresentation,
} from '../../features/notifications/lib/unreadBadge';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const NAV_LINK_END_PATHS = new Set(['/', '/feed', '/profile']);

function MobileOffcanvas({ isAuthed = false, badges = {} }) {
  const { isOffcanvasOpen, closeOffcanvas } = useNavigation();
  const panelRef = useRef(null);
  const lastFocusedRef = useRef(null);

  useEffect(() => {
    if (!isOffcanvasOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOffcanvasOpen]);

  useEffect(() => {
    if (!isOffcanvasOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeOffcanvas();
        return;
      }
      if (e.key !== 'Tab') return;

      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelectorAll(FOCUSABLE_SELECTOR);
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && (active === first || !panel.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !panel.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOffcanvasOpen, closeOffcanvas]);

  useEffect(() => {
    if (isOffcanvasOpen) {
      lastFocusedRef.current = document.activeElement;
      const panel = panelRef.current;
      const focusable = panel?.querySelectorAll(FOCUSABLE_SELECTOR);
      focusable?.[0]?.focus();
    } else if (lastFocusedRef.current?.focus) {
      lastFocusedRef.current.focus();
      lastFocusedRef.current = null;
    }
  }, [isOffcanvasOpen]);

  const items = getNavItems({
    zone: NAV_ZONES.OFFCANVAS,
    isAuthed,
    badges,
  });

  const inertProps = isOffcanvasOpen ? {} : { inert: '' };

  return (
    <>
      {isOffcanvasOpen ? (
        <div
          className="offcanvas-backdrop fade show"
          onClick={closeOffcanvas}
          aria-hidden="true"
        />
      ) : null}

      <div
        ref={panelRef}
        id={MOBILE_OFFCANVAS_ID}
        className={`offcanvas offcanvas-end mobile-more-menu ${isOffcanvasOpen ? 'show' : ''
          }`}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-more-menu-label"
        aria-hidden={!isOffcanvasOpen}
        {...inertProps}
      >
        <div className="offcanvas-header">
          <h2 className="offcanvas-title" id="mobile-more-menu-label">
            Меню
          </h2>
          <button
            type="button"
            className="btn-close"
            aria-label="Закрити"
            onClick={closeOffcanvas}
          />
        </div>

        <div className="offcanvas-body">
          <ul className="mobile-more__list">
            {items.map((item) => {
              const isNotification = item.badgeKey === 'unreadNotifications';
              // 99+ cap + exact link a11y name are notification-only.
              // Non-notification badges (e.g. unreadChats) keep raw pre-D1 values.
              const presentation = isNotification
                ? resolveNotificationNavPresentation(item.label, item.badge)
                : {
                    linkAriaLabel: undefined,
                    badge: item.badge
                      ? {
                          display: String(item.badge),
                          ariaLabel: `${item.badge} непрочитаних`,
                        }
                      : null,
                  };

              return (
              <li key={item.id}>
                <NavLink
                  to={item.to}
                  end={NAV_LINK_END_PATHS.has(item.to)}
                  className={({ isActive }) =>
                    `mobile-more__item${isActive ? ' active' : ''}`
                  }
                  aria-label={presentation.linkAriaLabel}
                  onClick={closeOffcanvas}
                >
                  <i className={item.iconClass} aria-hidden="true" />
                  <span className="mobile-more__label">{item.label}</span>
                  {presentation.badge ? (
                    <span
                      className="pmb-badge"
                      aria-hidden={isNotification ? 'true' : undefined}
                      aria-label={
                        isNotification ? undefined : presentation.badge.ariaLabel
                      }
                      data-unread-count={
                        isNotification
                          ? String(normalizeUnreadCount(item.badge))
                          : undefined
                      }
                    >
                      {presentation.badge.display}
                    </span>
                  ) : null}
                  <i
                    className="fa-solid fa-chevron-right mobile-more__chev"
                    aria-hidden="true"
                  />
                </NavLink>
              </li>
              );
            })}
          </ul>

          <div className="mobile-more__divider" />

          <ThemeToggle
            id="mobile-theme-checkbox"
            label="Темна тема"
            className="mobile-more__theme"
          />

          <p className="mobile-more__copyright">&#169; Рієстро</p>
        </div>
      </div>
    </>
  );
}

export default MobileOffcanvas;
